import _ from 'lodash'
import $ from 'jquery'
import calendars from 'scoreboard/components/calendars'
const { SPACED_EMDASH } = constants
import globals from 'scoreboard/components/globals'
import periodTitles, {single} from 'scoreboard/components/period-titles'
import config from 'scoreboard/components/config'
import breakdownRangeLabels from '../breakdown-range-labels'
import { getBreakdownPeriodLabel } from 'scoreboard/components/designer/breakdown-period-options'
import pieLike from '../pie-like'
import { applyPieSliceSubtitle } from './apply-pie-labels'
import prepareTrendLines from './prepare-trend-lines'
import applyFormatter from './apply-formatter'
import nonAxesPlotType from '../non-axes-plot-type'
import { enabled } from '../serie-drilldown'
import i18n from 'scoreboard/components/i18n'
import icon from 'scoreboard/components/icon'
import formatValue from 'scoreboard/components/format-value'
import { headerContextFilters, headerFilterContext } from 'scoreboard/datasets/overview/datasets-result'
import sections from 'scoreboard/components/sections'
import Dataset from 'scoreboard/datasets/model/dataset'
import * as formatterConverters from './formatter-converters'
const { toShortened } = formatterConverters
import { getGroupCalendar, getBreakdownCalendar } from '../group-or-breakdown-calendar'
import getYRange from './get-y-range'
import sortData from './sort-data'
import DataType from 'scoreboard/datasets/model/data-type'
const { NUMBER, PERCENT } = DataType

import constants from 'scoreboard/components/constants'
const { SCORED_TEXT } = constants

import findByUnderlyingSerieIdsFactory from 'scoreboard/charts/model/find-by-ids-factory'

const findByUnderlyingSerieIds = findByUnderlyingSerieIdsFactory({
	idAttr: 'underlyingSerieId',
	tempIdAttr: 'underlyingSerieTempId'
});

/**
 * Maps the plot type names used in Impact to the corresponding name in the charts
 * library
 */
const PLOT_TYPE_MAP = {
	'stacked-100-bar': 'bar',
	'filled-container': 'beaker',
	'tree-map': 'treemap'
}

/**
 * Returns data that can be passed to the chart module. Mostly uses what was
 * returned from the server.  (e.g. adding formatter functions)
 *
 * @param {ChartDesigner} chart - the chart designer model
 *
 * @param {ChartData} chartData - the actual data to be rendered on the chart
 *
 * @param {Object} [lastChartData] - when a chart is being re-rendered, the last
 * set of data produced by prepareData()
 *
 * @param {Boolean} [forChart] - true when this is called to prepare data for
 * a chart; false if it is for a data table
 */
function prepareData ({ chart, chartData, lastChartData, forChart = true }) {
	let prepared = {
		plots: _.cloneDeep(chartData.plots())
	}

	const labelsByPlotType = _.keyBy(chart.get('labels'), 'plotType')
	const fullHeight = useFullHeight(chart)

	applyAxesSettings(prepared, labelsByPlotType, chart)

	prepared.plots.forEach(plot => {
		let total
		if (pieLike(chart)) {
			// #36980 calculate the total of all pie slices when working with pie
			// charts
			total = _.sumBy(plot.series, serie => {
				return _.sumBy(serie.points, point => point && _.isNumber(point.raw) ? point.raw : 0)
			})
		}
		plot.series.forEach(serie => { // ChartDataSerie.kt - series AND features
			const labelSettings = labelsByPlotType[serie.type] ?
				// #52504 we don't want to affect the labels of other series by
				// modifying "labelsByPlotType", so make sure to clone here
				_.cloneDeep(labelsByPlotType[serie.type]) : null

			applyFormatter(serie, chartData, labelSettings, forChart)
			applyLabelSettings(serie, chart, total, labelSettings, forChart)

			// #36713 Set stack settings
			serie.offsetExpand = fullHeight[serie.axis]
			// #37535 for polar charts
			serie.fill = serie.fillRing || false

			setDefaultOpacity(serie)

			// Set the type to what the charts library expects after everything
			// else is processed
			// #41631 only update the plot type when this data is going to be sent
			// to the chart library
			if (forChart && PLOT_TYPE_MAP[serie.type]) {
				serie.type = PLOT_TYPE_MAP[serie.type]
			}

			applyClickable({ chart, dataSerie: serie })
		})

		setupBreakdowns(plot, chartData, chart)

		applyAxisFormat(prepared, chart, fullHeight, plot.series) // serie formatters must be assigned first

		adjustForSinglePeriodBards({ plot, chart })
	})

	sortData({ chart, chartData, preparedData: prepared, forChart })

	// Note: make sure trend lines are set up after data is sorted, or the
	// trend line won't reflect the plotted data
	prepareTrendLines({prepared, chart, chartData})

	applyTooltipSettings(prepared, chart)

	return prepared
}

/**
 * Set the opacity on a serie if it does not have one
 */
function setDefaultOpacity(serie) {
	// The chart library expects a value between 0 and 1 for opacity,
	// but we store it as a value between 1 and 100
	const defaultOpacityAdjusted = constants.DEFAULT_LINE_OPACITY / 10
	switch (serie.type) {
		case 'regression':

			serie.opacity = serie.opacity || defaultOpacityAdjusted
			break
		case 'reference-band':
			_.forEach(serie.lines, l => {
				if (l) {
					l.opacity = l.opacity || defaultOpacityAdjusted
				}
			})
			break
	}
}

/**
 * #36713 Determines if bar stacks should be full height for the left and right axes
 */
function useFullHeight(chart) {
	const features = chart.get('features')
	if (!features || !features.length) return false

	const [ leftFullHeight, rightFullHeight ] = _(features)
		.filter(f => f.type === 'stack')
		.partition(f => f.axis === 'left')
		.map(stacks => !!_.find(stacks, s => !!s.fullHeight))
		.value()

	return {
		left: leftFullHeight,
		right: rightFullHeight
	}
}

/**
 * #40655 configures the settings for the hover tooltips
 */
function applyTooltipSettings(prepared, chart) {
	prepared.tooltip = {
		totalLabel: i18n.get('common.total'),
		percentage: (value) => formatValue({ value: value, precision: 2, dataType: PERCENT }),
		headerFormatter: prepared.plots.length > 1
			// #41232 use special formatter for 2 breakdown charts; 0 or 1
			// breakdown charts are fine with the default header
			? _.partial(twoBreakdownTooltipHeaderFormatter, chart) : null
	}
}

/**
 * Returns a "headerFormatter" appropriate for charts with 2 breakdowns
 */
function twoBreakdownTooltipHeaderFormatter (chart, plot, range) {
	let plotTitle = _.escape(plot.breakdown.title)
	let rangeTitle = _.escape(([ range.title, range.subtitle ]).join(' '))

	function defaultHeader() {
		return [ plot.breakdown.title, rangeTitle ]
	}

	let firstSerie = chart.getSerieFeatures()[0]
	if (!firstSerie) {
		return defaultHeader()
	}

	// serie could use more than one dataset, but any one that it uses will
	// work for our purposes here
	let datasetId = _.first(firstSerie.datasetIds())
	if (!datasetId) {
		return defaultHeader()
	}

	let dataset = chart.data.datasetModel(datasetId)

	let plotField = getBreakdownField({ chart, firstSerie, dataset, sortOrder: 1 })
	let plotHeader =
		`<span class="sb-chart-header-label">${_.escape(plotField.label)}:</span>
		${plotTitle}`

	let rangeField = getBreakdownField({ chart, firstSerie, dataset, sortOrder: 0 })
	let rangeHeader =
		`<span class="sb-chart-header-label">${_.escape(rangeField.label)}:</span>
		${rangeTitle}`

	return [ plotHeader, rangeHeader ]
}

function getBreakdownField ({ chart, firstSerie, dataset, sortOrder }) {
	let breakdown = chart.breakdowns.find(b => b.get('sortOrder') == sortOrder)
	let fieldIds =
		// If there's a single dataset field used for all series, use that
		breakdown.getDatasetFieldIdentifier({ dataset }) ||
		// Just use dataset field for the first serie; if the breakdown is using
		// multiple fields, it's just too much to cram into a tooltip header
		breakdown.getDatasetFieldIdentifier({
			dataset,
			serieId: firstSerie.id,
			serieTempId: firstSerie.get('tempId')
		})

	return dataset.getFieldByFieldIdentifier(fieldIds)
}

/**
 * Apply settings that configure the chart axes
 */
function applyAxesSettings(prepared, labelsByPlotType, chart) {
	const infographic = {}
	const top = {}
	const bottom = {}
	const left = {}
	const right = {}
	const pie = {}
	const map = {}

	// Apply any general label settings

	const seriesPlotType = _.get(prepared, 'plots[0].series[0].type')
	const labelSettings = seriesPlotType && labelsByPlotType[seriesPlotType]
	if (labelSettings) {
		// Disable the "total" label at the bottom of the chart if the "charted values"
		// checkbox is turned off
		infographic.totalLabel = labelSettings.chartedValues || false
		infographic.stage = labelSettings.stageNumbers || false
		infographic.value = labelSettings.chartedValues || false
		infographic.percentages = labelSettings.percentsRemaining || false
		if (labelSettings.percentsRemaining) {
			// #40566 add formatter for the percents between stages
			infographic.remainingPercentageFormatter = value => {
				const formatted = formatValue({ value: value / 100, dataType: PERCENT, precision: 0 })
				return i18n.get('charts.chart.labels.percentsRemainingFormatted', formatted)
			}
		}
		if (pieLike(chart)) {
			// #37576 show or hide labels under repeating pie charts
			bottom.labels = chart.get('pieLabels') || false
		}
	}

	if (seriesPlotType === 'filled-map') map.back = createBackButton()[0]

	applyShowAllLabels({ bottom, top, chart })

	// Apply other settings

	// #37538 for waffles and filled containers, apply the total container value
	infographic.total = chart.get('totalContainerValue')
	infographic.equallySizedStages = !!chart.get('equallySizedStages')

	infographic.showRoot = !!chart.get('showRollupRoot')
	let drilldownId = _.get(prepared, 'plots[0].series[0].drilldownId')
	if (drilldownId) {
		// If we've done a drilldown, always show the root, so the user still sees
		// what they clicked on
		infographic.showRoot = true
	}

	const leftYRange = getYRange(chart, 'left')
	if (!_.isNil(leftYRange.max)) {
		left.yMaxForce = leftYRange.max
	}
	if (!_.isNil(leftYRange.min)) {
		left.yMinForce = leftYRange.min
	}

	const rightYRange = getYRange(chart, 'right')
	if (!_.isNil(rightYRange.max)) {
		right.yMaxForce = rightYRange.max
	}
	if (!_.isNil(rightYRange.min)) {
		right.yMinForce = rightYRange.min
	}

	left.labels = right.labels = chart.get('yaxisLabels') || false

	// #37576 This setting does something different for pie charts, so that's
	// handled above
	if (!pieLike(chart)) {
		bottom.labels = chart.get('xaxisLabels') || false
	}
	if (pieLike(chart) && labelSettings && (labelSettings.chartedValues || labelSettings.percentages)) {
		pie.value = true
	}

	;['left', 'right'].forEach(axis => {
		applyDiscreteTicks({
			chart,
			axis,
			axisSettings: axis === 'left' ? left : right
		})
	})

	prepared.axes = { infographic, top, bottom, left, right, pie, map }
}

function applyShowAllLabels ({ bottom, top, chart }) {
	// Only disable "show all labels" logic if all of the following are true:
	//  - a breakdown exists (if no breakdowns, the x axis is showing the
	//    series names, and we don't want to hide them)
	//  - the corresponding breakdown is by date (#42592)
	//  - more than one period is showing (#43146)
	function showForBreakdown (sortOrder) {
		let b = chart.breakdowns.findBySortOrder(sortOrder)
		if (!b) {
			return true
		}

		return isSinglePeriod(b)
	}

	// Bottom axis is showing the first breakdown
	bottom.showAllLabels = showForBreakdown(0)
	// Top axis is showing the second breakdown
	top.showAllLabels = showForBreakdown(1)
}

function applyDiscreteTicks ({ chart, axis, axisSettings }) {
	// #49550 if all series on the axis are used scored text, set discreteTicks
	// so the Y axis ticks line up correctly
	let scoringTypeIds = _(chart.getSerieFeatures())
		.filter(s => s.get('axis') === axis)
		.filter(s => {
			let source = s.get('source')
			return source.domainType === 'scorecard-item'
				// Note: if a scored text metric is using the 'score' series,
				// we don't need discreteTicks (because it's just a number)
				&& source.dataProperty === 'value'
		})
		.map(s => {
			let sn = chart.data.scorecards(s.get('source').scorecardItemId)
			return sn?.scoringTypeId
		})
		.compact()
		.uniq()
		.value()

	// We can only render the Y axis ticks as text if all metrics are using
	// the same scoring type
	if (scoringTypeIds.length != 1) {
		return
	}

	let scoringType = config.scoringType(scoringTypeIds)

	if (scoringType?.kpiValueType == SCORED_TEXT) {
		// #49550 If we're only using a single scoring type, and that type is
		// scored text, then we set discreteTicks so that we'll have a
		// horizontal tick for each possible value. We use the threshold scores,
		// since that's what we use for the point's value (#46641)
		axisSettings.discreteTicks = scoringType.thresholds.map(t => t.score)
	}
}

function isSinglePeriod(breakdown) {
	if (_.isNil(breakdown)) return false
	// #44750 if this isn't a calendar breakdown, then it is equivalent to having
	// a single period
	if (!breakdown.isCalendarPeriodBreakdown()) return true
	const ranges = breakdown.attributes.ranges
	if (_.isEmpty(ranges) || ranges.length > 1) return false
	const range = ranges[0]
	if (!_.isNil(range.endPeriodDelta) && range.endPeriodDelta !== range.startPeriodDelta) return false
	if (!_.isNil(range.endPeriodId) && range.endPeriodId !== range.startPeriodId) return false
	return true
}

/**
 * Sets a numeric axis level formatter if series dataTypes are not homogeneous.
 * Exception made for 100% stacks
 *
 * https://github.com/SpiderStrategies/Scoreboard/issues/40517
 * https://github.com/SpiderStrategies/Scoreboard/issues/41742#issuecomment-925048314
 */
function applyAxisFormat(prepared, chart, fullHeight, plotSeries) {
	const plotSeriesByAxis = _.groupBy(plotSeries, 'axis')

	const formatter = dataType => {
		return (value) => formatValue({value, dataType, precision: 2})
	}
	const maybeApplyNumericOverride = axis => {
		if (fullHeight[axis]) {
			// When a stack is 100% height any other series disappear, so this must
			// take precedence over the numeric override.
			prepared.axes[axis].formatter = formatter(PERCENT)
			return
		}

		const dataSeries = _.filter(plotSeriesByAxis[axis],
				plotSerie => chart.findFeatureByUnderlyingSerieId(plotSerie).isSerie())
		// Because initiatives don't have an explicit dataType we must apply
		// each serie's formatter to determine the distinct formatter output.
		// 1 is used because it will apply to Yes/No values
		const formatterResults = _.map(dataSeries, s => _.invoke(s, 'formatter', 1))
		const distinctFormats = _.size(_.uniq(formatterResults))

		if (distinctFormats > 1) {
			prepared.axes[axis].formatter = formatter(NUMBER)
		} else if (distinctFormats === 1) {
			// the chart api will just set the raw value (e.g. numeric) if a
			// serie doesn't have a formatter; this breaks things if the last
			// serie has no formatter (e.g. reference-bands)
			// So we will just always set a y-axis override in Impact
			const firstSerie = _.find(dataSeries, s => !_.isNil(s.formatter))
			if (firstSerie) {
				// #42099 always abbreviate axes values
				prepared.axes[axis].formatter =
					toShortened(firstSerie.shortenedAndUnshortenedFormatter)
			}
		}
	}

	maybeApplyNumericOverride('left')
	maybeApplyNumericOverride('right')

	if (chart.get('plotType') === 'filled-container') {
		// #41936 format the numbers up the side of the container; just use
		// the formatter for the first serie
		const formatter = _.get(prepared, 'plots[0].series[0].formatter')
		if (formatter) {
			prepared.axes.infographic.formatter = formatter
		}
	}
}

/**
 * #12381 #19988 #42032 for single period bar charts, adjust the data so that
 * the chart module will render a label for each bar (instead of using a
 * single period label for all the bars
 */
function adjustForSinglePeriodBards({ plot, chart }) {
	// Only do this if every serie is a bar
	// #42922 look only at series, not features, so we don't take scoring bands,
	// trend lines, etc into account
	let series = chart.features.filter(f => f.get('type') == 'serie')
	if (series.find(f => f.get('plotType') !== 'bar')) {
		return
	}

	// This only needs to be done if there's a single breakdown using calendar
	// period ranges
	if (chart.breakdowns.length != 1) {
		return
	}

	let breakdown = chart.breakdowns.at(0)
	if (breakdown.get('breakdownBy') !== 'calendar-period-ranges') {
		return
	}

	if (breakdown.ranges.length != 1) {
		return
	}

	let range = breakdown.ranges.at(0)

	// And this is only done if that single calendar period range uses a
	// single period (relative or absolute)
	let singlePeriod
	if (!_.isNil(range.get('startPeriodId'))) {
		singlePeriod = range.get('startPeriodId') == range.get('endPeriodId')
	} else {
		singlePeriod = range.get('startPeriodDelta') == range.get('endPeriodDelta')
	}

	if (!singlePeriod) {
		return
	}

	// Adjust the data so that the chart module sees it as a zero-breakdown
	// chart, which will give each bar its own label. This basically just
	// means there are no breakdown ranges
	plot.breakdown.range = []
	plot.series.flatMap(s => s.points).forEach(s => delete s.rangeId)
}

/**
 * Setup breakdown labels
 */
function setupBreakdowns(plot, chartData, chart) {
	plot.breakdown.title = breakdownTitle({
		chartData,
		chart,
		breakdown: plot.breakdown
	})

	let useGenericBreakdownPeriodLabels = !breakdownPeriodLabelsMatch(plot.breakdown.range)

	_.forEach(plot.breakdown.range, range => {
		_.extend(range, rangeOrBreakdownTitle({
			chartData,
			chart,
			useGenericBreakdownPeriodLabels,
			dataRangeOrBreakdown: range,
			unabbreviated: nonAxesPlotType(chart)
		}))

		_.forEach(range.nonAggregatedDataRanges, range => {
			_.extend(range, rangeOrBreakdownTitle({
				chartData,
				chart,
				useGenericBreakdownPeriodLabels,
				dataRangeOrBreakdown: range
			}))
		})
	})
}

/**
 * Apply label settings that are specific to each serie.
 */
function applyLabelSettings(serie, chart, total, labelSettings, forChart) {
	let setTitle = false

	if ((labelSettings && labelSettings.seriesLabels) || serie.type === 'tree-map') {
		setTitle = true
	}

	if ([ 'line', 'bar', 'area', 'regression' ].includes(serie.type)) {
		// #40652 lines, bars, and areas need the title set for the tooltip to show
		// the serie label. They don't have label options, so we can add the title
		// worrying that it will show where it's not suppose to
		setTitle = true
	}

	if (serie.type === 'tree-map') {
		// treemaps always need labels; there are no label settings, and they'd
		// be useless without them
		setTitle = true
	}

	if (!forChart) {
		// Title should always be set for the data table (though the data table
		// code may decide not to display it)
		setTitle = true
	}

	if (setTitle) {
		let underlyingSerie = chart.findFeatureByUnderlyingSerieId(serie)

		if (chart.get('plotType') === 'tree-map') {
			setRollupSerieLabel({ chart, serie, underlyingSerie })
		} else if (serie.type === 'regression') { // trend lines require the line label
			serie.title = _.get(underlyingSerie.getChildren({type: 'line'}), '[0].label')
		} else if (serie.type === 'series' && chart.hasGroupedRangesBreakdown()) {
			serie.title = getSerieLabelForGroupedBreakdown(
				{ chart, dataSerie: serie, underlyingSerie })
		}

		if (!serie.title) {
			// But if there is no title assigned for things like trend lines use the feature label
			serie.title = chart.featureLabel({feature: underlyingSerie})
		}
	}

	if (!labelSettings) return

	if (pieLike(chart)) {
		// #36980 handle special formatting of the subtitle for pie slices
		applyPieSliceSubtitle(serie, total, labelSettings)
	}

	if (_.includes(['bar', 'line', 'area'], serie.type) && (labelSettings.seriesLabels || labelSettings.chartedValues)) {
		// #27591 for bar charts, display the value and the serie name
		// for each individual bar
		const abbreviate = labelSettings.abbreviateChartedValues
		const shorten = _.isNil(abbreviate) || abbreviate
		_.forEach(serie.points, point => {
			// #43932 bars need a special setup to display the label and value
			// separately
			point.formatted = {}
			if (labelSettings.seriesLabels) {
				point.formatted.title = serie.title
			}
			if (labelSettings.chartedValues) {
				// #27591 we can't just use the normal serie.formatter here as it is
				// set to always be "unshortened" regardless of label settings
				const shortenedAndUnshortened = serie.shortenedAndUnshortenedFormatter(point.raw)
				let formatted = shortenedAndUnshortened[shorten ? 'shortened' : 'unshortened']
				point.formatted.value = formatted
			}
		})
	}

	const description = getSerieDescription(serie, labelSettings, chart)
	if (description) {
		serie.subtitle = description
	}

}

/**
 * Returns an appropriate label for the given serie based on the other series
 * in the grouped calendar breakdown chart. See #42000 for full explanation
 * of the rules
 */
function getSerieLabelForGroupedBreakdown ({ chart, dataSerie, underlyingSerie }) {
	dataSerie = dataSerie || getCorrespondingDataSerie({ chart, underlyingSerie })
	let customLabel = underlyingSerie.get('label')
	if (customLabel) {
		// If a custom label was specified, always use that
		return customLabel
	}

	let labelParts = []

	addDomainObjectIfNecessary({ chart, underlyingSerie, labelParts })
	addPeriodIfNecessary({ chart, dataSerie, underlyingSerie, labelParts })

	if (labelParts.length > 0) {
		return labelParts.join(SPACED_EMDASH)
	}

	// Fallback in case the above process didn't generate any labels
	return chart.featureLabel({feature: underlyingSerie})
}

function addDomainObjectIfNecessary ({ chart, underlyingSerie, labelParts }) {
	let distinctDomainObjectLabels = _(chart.getSerieFeatures())
		.map(s => chart.featureLabel({ feature: s }))
		.uniq()
		.value()

	if (distinctDomainObjectLabels.length > 1) {
		// If there's more than one "auto" feature label on this chart, then
		// we need to include the auto generated labels to help distinguish
		// the series
		labelParts.push(chart.featureLabel({ feature: underlyingSerie }))
	}
}

function addPeriodIfNecessary ({ chart, dataSerie, underlyingSerie, labelParts }) {
	let distinctPeriodIds = _(chart.getSerieFeatures())
		.flatMap(s => {
			if (s.domainType() === 'dataset') {
				let ds = getCorrespondingDataSerie({ chart, underlyingSerie: s })
				return _.get(ds, 'datasetGroupPeriods') || []
			} else {
				return s.periods.map(p => {
					let range = chart.breakdowns.first().ranges.first()
					return p.resolvedPeriodId({
						currentPeriodId: globals.periodId,
						range
					})
				})
			}
		})
		.uniq()
		.value()

	if (distinctPeriodIds.length > 1) {
		// If there's more than one group period on this chart, then we need to
		// include the period name to help distinguish the series
		let periodIds = []
		if (underlyingSerie.domainType() === 'dataset') {
			periodIds = dataSerie.datasetGroupPeriods
		} else {
			let p = underlyingSerie.periods.at(0)
			if (p) {
				let range = chart.breakdowns.first().ranges.first()
				let periodId = p.resolvedPeriodId({
					currentPeriodId: globals.periodId,
					range
				})
				periodIds = periodId ? [periodId] : []
			}
		}

		let periodLabels = _(periodIds).map(periodId => {
			let period = calendars.periodsById[periodId]
			if (period) {
				let titles = periodTitles([ period ])[0]
				let periodTitle = titles.title
				if (titles.subtitle) {
					periodTitle += ' ' + titles.subtitle
				}

				return periodTitle
			}
		}).compact().value()

		if (periodLabels.length > 0) {
			labelParts.push(periodLabels.join(', '))
		}
	}
}

/**
 * For a given chart and serie, returns a corresponding serie from the ChartData.
 *
 * @param {ChartDesigner} chart
 * @param {ChartDesignerSerie} underlyingSerie
 * @returns {ChartDataSerie}
 */
function getCorrespondingDataSerie ({ chart, underlyingSerie }) {
	// Just use the corresponding serie from the first plot
	let dataSeries = _.get(chart.data.plots(), '[0].series')
	return findByUnderlyingSerieIds({
		id: underlyingSerie.id,
		tempId: underlyingSerie.get('tempId'),
		collection: dataSeries
	})
}

function getSerieDescription(serie, labelSettings, chart) {
	switch (labelSettings.descriptionType) {
		case 'custom':
			return labelSettings.customDescription
			break
		case 'scorecard':
			const scorecard = chart.data.scorecards(serie.source.scorecardItemId)
			return _.get(scorecard, 'description')
			break
		case 'initiative':
			const initiative = chart.data.initiatives(serie.source.initiativeId)
			return _.get(initiative, 'description')
			break
	}
	return null
}

const DOMAIN_TYPE_ID_MAP = {
	'scorecard-item': 'scorecardItemId',
	'initiative': 'initiativeId'
}

/**
 * Gets the domain object's dataType corresponding to the serie's data source
 *
 * @return {String} the datatype ('number', 'percent', 'currency')
 */
function getSerieDomainObjectDataType(serie, chart) {
	const { domainType, aggregation } = serie.attributes.source
	const idField = DOMAIN_TYPE_ID_MAP[domainType]
	let dataType
	if (idField) {
		const id = serie.attributes.source[idField]
		const section = sections.fromType(domainType)
		dataType = chart.data.domainObject(section, id).dataType
	} else if (aggregation) {
		const { startingDatasetId, fieldId} = aggregation
		const dataset = chart.data.datasetModel(startingDatasetId)
		if (dataset) {
			if (Dataset.isTotal(fieldId)) {
				dataType = NUMBER
			} else {
				const field = dataset.getUnderlyingField(fieldId) || {}
				dataType = field.dataType
			}
		}
	}
	return dataType
}

function setRollupSerieLabel ({ chart, serie, underlyingSerie }) {
	let aggregation = underlyingSerie.get('source').aggregation
	if (!aggregation || !aggregation.startingDatasetId) {
		serie.title = chart.featureLabel({feature: underlyingSerie})
		return
	}

	let dataset = chart.data.datasetModel(aggregation.startingDatasetId)
	let underlyingField = dataset.getUnderlyingField(underlyingSerie.get('datasetRollupFieldId'))

	let drilldownFilters = _.get(chart.data.currentDrilldown(), 'drilldownFilters') || []

	let headerFilters = headerContextFilters(_.filter(drilldownFilters, f => {
		// For determining the "header text" and back button visibility, we only
		// care about filters on the serie's rollup field
		return dataset.getUnderlyingField(f.fieldId)?.id == underlyingField.id
	}))

	let filtersByLinkGroup = _(headerFilters)
		.filter(f => !_.isNil(f.linkGroup))
		.groupBy('linkGroup')
		.value()

	let filterContext = headerFilterContext({
		filters: headerFilters,
		dataset,
		underlyingField,
		treeItemsById: chart.data.treeItems(),
		filtersByLinkGroup,
		parentId: chart.data.rollupParentId()
	})

	if (filterContext.back) {
		// If the current filter state supports the Back option, pass in a DOM
		// node for the button
		serie.backNode = createBackButton()[0]

		// Use the same label for the serie that is used in the dataset table
		// for the back button
		// https://github.com/SpiderStrategies/Scoreboard/issues/40252#issuecomment-918185014
		// It's in HTML, though, so just pull out the plain text
		serie.title = $(`<span>${filterContext.filterText}</span>`).text()
	} else {
		// If there's no Back option, just render the configured serie title
		serie.title = chart.featureLabel({feature: underlyingSerie})
	}
}

function createBackButton () {
	let backIcon = icon('back')
	let button =
		`<div class="button-container">
			<button type="button" class="button button-default x-small">
				${backIcon} ${i18n.get('common.back')}
			</button>
		</div>`
	return $(button)
}

/**
 * Returns true if the "contextless" part of the labels for the breakdown periods
 * in all of the ranges matches. Examples:
 *
 *  - A range is set group Yearly, breakdown Monthly, and its periods are
 *    January 2018 and January 2019. The January parts match, so this returns true
 *
 *  - A range is set group Quarterly, breakdown Monthly, and its periods are
 *    January 2018 and April 2018. The January/April parts do not match, so this
 *    returns false
 *
 * @param {Object[]} ranges - the JS serialization of a list of
 * CalendarPeriodGroupedDataRange from the server
 */
function breakdownPeriodLabelsMatch (ranges) {
	let mismatch = false
	_.forEach(ranges, range => {
		let distinctTitles = _(range.breakdownPeriodIdsByGroupPeriodId)
			// Get all the breakdown periods for this range
			.values()
			// And get the "title" part of each one (eg "January" for "January 2018")
			.map(p => {
				if (!p) {
					return null
				}

				return single(calendars.periodsById[p]).title
			})
			// Remove any nulls
			.compact()
			// Get unique titles
			.uniq().value()

		// If not all the generic "title" parts of the period labels were the same,
		// we have a mismatch
		if (distinctTitles.length > 1) {
			mismatch = true
		}
	})

	return !mismatch
}

function breakdownTitle ({ chartData, chart, breakdown }) {
	if (!breakdown.underlyingRangeId && !breakdown.underlyingRangeTempId) {
		// If there's no underlying range, then we're working with 0 or 1 breakdowns,
		// and there isn't an applicable breakdown title
		return null
	}

	return rangeOrBreakdownTitle({
		chartData,
		chart,
		dataRangeOrBreakdown: breakdown,
		unabbreviated: true
	}).title
}

/**
 * Returns the title to use for a given range or breakdown
 *
 * @param {ChartData} chartData
 * @param {ChartDesigner} chart
 * @param {DataRangeOrBreakdown} dataRangeOrBreakdown - either the ChartDataBreakdown
 * or ChartDataRange that we want the label for
 *
 * @returns {{title: String, subtitle: String}} the title/subtitle to use
 *
 * @typedef DataRangeOrBreakdown
 *
 * @property {Number} [underlyingRangeId] the id of the breakdown range associated
 * with this data breakdown/range
 *
 * @property {String} [underlyingRangeTempId] the tempId of the breakdown range
 * associated with this data breakdown/range
 *
 * @property {Number} [periodId] for a breakdown based on calendar periods, the
 * corresponding periodId
 *
 * @property {Boolean} [useGenericBreakdownPeriodLabels=false] only applicable
 * to a calendar-period-grouped range: if true, will use generic labels like
 * "Period 1" and "Period 2" or "Month 1" and "Month 2". If false, will use
 * more specific contextless labels, like "January" or "February"
 */
function rangeOrBreakdownTitle ({ chartData, chart, dataRangeOrBreakdown, useGenericBreakdownPeriodLabels, unabbreviated }) {
	const plotType = chart.get('plotType')
	if (plotType === 'tree-map' || plotType === 'filled-map' ||
		plotType === 'spike-map' || plotType === 'bubble-map') {
		// Dataset tree item labels don't require i18n, so we just set them
		// directly on the range on the server for tree maps
		return { title: dataRangeOrBreakdown.title }
	}

	let underlyingRange = chart.breakdownRange({
		id: dataRangeOrBreakdown.underlyingRangeId,
		tempId: dataRangeOrBreakdown.underlyingRangeTempId
	})

	if (underlyingRange.get('type') === 'calendar-period') {
		return standardCalendarPeriodRangeOrBreakdownTitle({ dataRangeOrBreakdown, unabbreviated })
	} else if (underlyingRange.get('type') === 'calendar-period-grouped') {
		return {
			title: groupedRangeOrBreakdownTitle(
				{ dataRangeOrBreakdown, useGenericBreakdownPeriodLabels })
		}
	} else {
		let underlyingBreakdown = chart.breakdowns.find(b => {
			return !!b.ranges.find(r => r === underlyingRange)
		})

		return {
			title: breakdownRangeLabels({
				data: chartData,
				breakdown: underlyingBreakdown,
				range: underlyingRange
			}).label
		}
	}
}

/**
 * @param {Boolean} unabbreviated #39831 when true, the full period label is returned,
 * otherwise an abbreviated version is
 */
function standardCalendarPeriodRangeOrBreakdownTitle ({ dataRangeOrBreakdown, unabbreviated }) {
	if (dataRangeOrBreakdown.nonAggregatedDataRanges) {
		// nonAggregatedDataRanges means this is a "One Chart" pie, and the range
		// titles are never shown on the chart, so it's safe to just return an
		// empty string (but not null, because the chart code chokes on that)
		return ''
	}
	let period = calendars.periodsById[dataRangeOrBreakdown.periodId]
	if (unabbreviated) return { title: period.label }

	let titles = periodTitles([ period ])[0]
	return _.pick(titles, 'title', 'subtitle')
}

function groupedRangeOrBreakdownTitle ({ dataRangeOrBreakdown, useGenericBreakdownPeriodLabels }) {
	if (dataRangeOrBreakdown.nonAggregatedDataRanges) {
		// nonAggregatedDataRanges means this is a "One Chart" pie, and the range
		// titles are never shown on the chart, so it's safe to just return an
		// empty string (but not null, because the chart code chokes on that)
		return ''
	}

	if (_.isEmpty(dataRangeOrBreakdown.breakdownPeriodIdsByGroupPeriodId)) {
		// #42240 dataset serie may not have a filter that can be used to
		// populate breakdownPeriodIdsByGroupPeriodId. In that scenario, just
		// use the periodIndex/periodDelta and use the generic period labels
		return getBreakdownPeriodLabel({
			groupCalendarType: getGroupCalendar(dataRangeOrBreakdown).type,
			breakdownCalendarType: getBreakdownCalendar(dataRangeOrBreakdown).type,
			index: dataRangeOrBreakdown.periodIndex,
			periodDelta: dataRangeOrBreakdown.periodDelta
		})
	}

	if (useGenericBreakdownPeriodLabels) {
		const [groupCalendarType, breakdownCalendarType] =
			_(dataRangeOrBreakdown.breakdownPeriodIdsByGroupPeriodId)
				.map((breakdownPeriodId, groupPeriodId) => [
					groupPeriodId, breakdownPeriodId
				])
				// We just grab the first group/breakdown period ID pair
				// (two-item array), since we are not interested in specific periods
				// but in their calendar types
				.head()
				.map(periodId => parseInt(periodId))
				.map(periodId => calendars.periodsById[periodId].calendar.type)
		return getBreakdownPeriodLabel({
			groupCalendarType, breakdownCalendarType,
			index: dataRangeOrBreakdown.periodIndex,
			periodDelta: dataRangeOrBreakdown.periodDelta
		})
	} else {
		// We know the "generic" part of each period name is the same, so
		// get the first non-null one and use it
		return _(dataRangeOrBreakdown.breakdownPeriodIdsByGroupPeriodId)
			.values()
			.compact()
			.map(p => {
				let period = calendars.periodsById[p]
				return periodTitles.single(period).title
			})
			.head()
	}
}

const CLICKABLE_SERIE_PLOT_TYPES = [ 'waffle', 'filled-container', 'timeline',
	'funnel', 'cycle', 'process', 'pyramid' ]

function applyClickable ({ chart, dataSerie }) {
	if (!_.includes(CLICKABLE_SERIE_PLOT_TYPES, chart.get('plotType'))) {
		return
	}

	// #45607 any infographic serie that can be clicked for drilldown navigation
	// (not the same as a treemap drilldown that updates the chart) should have
	// clickable: true so it can by styled appropriately
	let underlyingSerie = chart.findFeatureByUnderlyingSerieId(dataSerie)
	if (enabled({ serie: underlyingSerie })) {
		dataSerie.clickable = true
	}
}

_.extend(prepareData, {
	getSerieLabelForGroupedBreakdown,
	applyShowAllLabels,
	applyLabelSettings
})
export default prepareData
export {
	getSerieLabelForGroupedBreakdown,
	applyShowAllLabels,
	applyLabelSettings
}

prepareData.__filename__ = 'charts/chart-data/prepare-data.js'
